import { useFormikContext } from 'formik'

import { useDeliveryMethodModal } from '@b-stock/fe-core/bstock-next/components'
import type {
  OrchestratedOfferingListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { centsToDollars, FormattedMessage } from '@b-stock/fe-core/bstock-react'

import { type MakeAnOfferFormValues } from '@/buyer-portal/components/features/listing/ListingActionColumn/offering/SimpleOfferActionModule/MakeAnOfferFormProvider'
import { SimpleOfferInput } from '@/buyer-portal/components/features/listing/ListingActionColumn/offering/SimpleOfferActionModule/SimpleOfferInput'
import { getEditDeliveryMethodRedirectUri } from '@/buyer-portal/helpers/shipping'
import { useIsMobile } from '@/buyer-portal/hooks/layout'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useOfferAnalytics } from '../../offering/useOfferAnalytics'
import { ActionButton, ActionCardInput } from '../ui'

const ns = 'Listing.actionColumn.actionCard.offer'
// V1 offer copy reused for parity (input label + noQuote label).
const v1ns = 'Listing.actionColumn.simpleOfferActionModule'

/**
 * Offer amount input + submit. An offer already in play submits as an update,
 * which the offers API only accepts while the offer window is open. Without a
 * delivery quote the offer can't be priced, so the action routes to the
 * delivery-method modal first.
 */
export const SimpleOfferSubmit = ({
  disabled,
  listing,
  selectedQuote,
}: {
  disabled?: boolean
  listing: OrchestratedOfferingListingDetails
  selectedQuote?: OrchestratedQuote | null
}) => {
  const mobileScreen = useIsMobile()
  const { deliveryMethodChangePending, editDeliveryMethod } =
    useDeliveryMethodModal()
  const { values } = useFormikContext<MakeAnOfferFormValues>()
  const { trackButtonClicked } = useAnalytics()
  const getOfferAnalytics = useOfferAnalytics(listing)

  const hasOffer = !!listing.offers?.length
  const isDisabled = disabled || deliveryMethodChangePending
  const size = mobileScreen ? 'medium' : 'large'

  if (!selectedQuote) {
    return (
      <ActionButton
        appearance={deliveryMethodChangePending ? 'loading' : 'primary'}
        disabled={isDisabled}
        onClick={() => {
          editDeliveryMethod({
            redirectURI: getEditDeliveryMethodRedirectUri(),
          })
          trackButtonClicked({
            buttonName: 'select_shipment_to_offer',
            ...getOfferAnalytics(),
          })
        }}
        size={size}
        type="button"
      >
        <FormattedMessage id={`${v1ns}.button.noQuote`} />
      </ActionButton>
    )
  }

  return (
    <>
      <ActionCardInput>
        <SimpleOfferInput
          listing={listing}
          ns={v1ns}
          disabled={isDisabled}
          shortUnit
        />
      </ActionCardInput>

      <ActionButton
        appearance={
          deliveryMethodChangePending
            ? 'loading'
            : hasOffer
              ? 'secondary'
              : 'primary'
        }
        disabled={isDisabled}
        onClick={() =>
          trackButtonClicked({
            buttonName: hasOffer ? 'update_offer' : 'make_offer',
            offer_amt_usd: centsToDollars(values.amount ?? 0),
            ...getOfferAnalytics(),
          })
        }
        size={size}
        type="submit"
      >
        <FormattedMessage
          id={`${ns}.actions.${hasOffer ? 'updateOffer' : 'makeOffer'}`}
        />
      </ActionButton>
    </>
  )
}
